import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { ADMIN_ROUTES, POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { Button } from "@/shared/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/shared/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from "@/shared/components/ui/sidebar";

const NAV_SECTIONS = [
  {
    title: "Foundation",
    path: ADMIN_ROUTES.STYLE_GUIDE_FOUNDATION,
    items: [
      { id: "colors", label: "Colors" },
      { id: "typography", label: "Typography" },
    ],
  },
  {
    title: "Form Controls",
    path: ADMIN_ROUTES.STYLE_GUIDE_FORM_CONTROLS,
    items: [
      { id: "address-picker", label: "Address Picker" },
      { id: "button-v3", label: "Button" },
      { id: "calendar", label: "Calendar" },
      { id: "checkbox", label: "Checkbox" },
      { id: "combobox", label: "Combobox" },
      { id: "date-picker", label: "Date Picker" },
      { id: "icon-button", label: "Icon Button" },
      { id: "input", label: "Input" },
      { id: "label", label: "Label" },
      { id: "multi-select", label: "Multi Select" },
      { id: "phone-number-input", label: "Phone Number Input" },
      { id: "radio-group", label: "Radio Group" },
      { id: "select", label: "Select" },
      { id: "switch", label: "Switch" },
      { id: "textarea", label: "Textarea" },
      { id: "toggle-group", label: "Toggle Group" },
    ],
  },
  {
    title: "Data Display",
    path: ADMIN_ROUTES.STYLE_GUIDE_DATA_DISPLAY,
    items: [
      { id: "animated-shiny-text", label: "Animated Shiny Text" },
      { id: "badge", label: "Badge" },
      { id: "card", label: "Card" },
      { id: "document-badge", label: "Document Badge" },
      { id: "progress", label: "Progress" },
      { id: "security-note", label: "Security Note" },
      { id: "skeleton", label: "Skeleton" },
      { id: "table", label: "Table" },
      { id: "tag", label: "Tag" },
    ],
  },
  {
    title: "Feedback",
    path: ADMIN_ROUTES.STYLE_GUIDE_FEEDBACK,
    items: [
      { id: "callout", label: "Callout" },
      { id: "toast", label: "Toast" },
    ],
  },
  {
    title: "Layout",
    path: ADMIN_ROUTES.STYLE_GUIDE_LAYOUT,
    items: [
      { id: "accordion", label: "Accordion" },
      { id: "collapsible", label: "Collapsible" },
      { id: "separator", label: "Separator" },
      { id: "tabs", label: "Tabs" },
    ],
  },
  {
    title: "Overlays",
    path: ADMIN_ROUTES.STYLE_GUIDE_OVERLAYS,
    items: [
      { id: "command", label: "Command" },
      { id: "context-menu", label: "Context Menu" },
      { id: "dialog", label: "Dialog" },
      { id: "dropdown-menu", label: "Dropdown Menu" },
      { id: "popover", label: "Popover" },
      { id: "sheet", label: "Sheet" },
      { id: "tooltip", label: "Tooltip" },
    ],
  },
  {
    title: "Custom",
    path: ADMIN_ROUTES.STYLE_GUIDE_CUSTOM,
    items: [
      { id: "postloss-paywall", label: "PostLossPaywall" },
      { id: "confirm-delete-dialog", label: "ConfirmDeleteDialog" },
      { id: "people-selector", label: "PeopleSelector" },
    ],
  },
  {
    title: "Upload",
    path: ADMIN_ROUTES.STYLE_GUIDE_UPLOAD,
    items: [
      { id: "upload-zone", label: "Upload Zone" },
      { id: "file-card", label: "File Card" },
      { id: "document-upload-input", label: "Document Upload Input" },
      { id: "upload-method-cards", label: "Upload Method Cards" },
      { id: "uploaded-document", label: "Uploaded Document" },
      { id: "qr-upload-instructions", label: "QR Upload Instructions" },
      { id: "contact-profile-picture", label: "Contact Profile Picture" },
      { id: "upload-documents-form", label: "Upload Documents Form" },
      { id: "upload-document-modal", label: "Upload Document Modal" },
      { id: "upload-documents-dialog", label: "Upload Documents Dialog" },
      { id: "mobile-upload-modal", label: "Mobile Upload Modal" },
      { id: "upload-limit-modal", label: "Upload Limit Modal" },
      { id: "lawyer-upload-dialog", label: "Lawyer Upload Dialog" },
      { id: "death-certificate-step", label: "Death Certificate Step" },
      { id: "will-upload-step", label: "Will Upload Step" },
      { id: "upload-id", label: "Upload ID (onboarding)" },
      { id: "tax-return-upload", label: "Tax Return Upload" },
    ],
  },
  {
    title: "Auth",
    path: ADMIN_ROUTES.STYLE_GUIDE_AUTH,
    items: [{ id: "email-verification", label: "Email Verification" }],
  },
  {
    title: "Integrations",
    path: ADMIN_ROUTES.STYLE_GUIDE_INTEGRATIONS,
    items: [
      { id: "contact-enrich", label: "Contact Enrich Plus (Enformion)" },
      { id: "document-request-test", label: "Document Request" },
    ],
  },
];

type NavSection = (typeof NAV_SECTIONS)[number];

const SCROLL_SPY_OFFSET = 120;

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

const StyleGuideLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const filterRef = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = React.useState("");
  const [openSections, setOpenSections] = React.useState<Set<string>>(new Set());
  const [spiedItemId, setSpiedItemId] = React.useState<string | null>(null);

  const isActiveSection = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  const activeSection = NAV_SECTIONS.find((section) => isActiveSection(section.path));

  React.useEffect(() => {
    setOpenSections(new Set(activeSection ? [activeSection.title] : []));
  }, [activeSection]);

  React.useEffect(() => {
    if (location.hash) {
      // Small delay to ensure the page has rendered
      setTimeout(() => {
        document.getElementById(location.hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  React.useEffect(() => {
    const items = activeSection?.items ?? [];
    const updateSpiedItem = () => {
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      const passed = items.filter(
        (item) =>
          (document.getElementById(item.id)?.getBoundingClientRect().top ?? Infinity) <=
          SCROLL_SPY_OFFSET,
      );
      const current = isAtBottom && passed.length > 0 ? items.at(-1) : (passed.at(-1) ?? items[0]);
      setSpiedItemId(current?.id ?? null);
    };

    updateSpiedItem();
    window.addEventListener("scroll", updateSpiedItem, { passive: true });
    return () => window.removeEventListener("scroll", updateSpiedItem);
  }, [activeSection]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "/" && !isEditableTarget(event.target)) {
        event.preventDefault();
        filterRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const isFiltering = normalizedQuery.length > 0;
  const visibleSections: NavSection[] = isFiltering
    ? NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.title.toLowerCase().includes(normalizedQuery)
          ? section.items
          : section.items.filter((item) => item.label.toLowerCase().includes(normalizedQuery)),
      })).filter((section) => section.items.length > 0)
    : NAV_SECTIONS;

  const handleSectionClick = (section: NavSection) => {
    if (isActiveSection(section.path)) {
      setOpenSections((prev) => {
        const next = new Set(prev);
        if (next.has(section.title)) {
          next.delete(section.title);
        } else {
          next.add(section.title);
        }
        return next;
      });
    } else {
      navigate(section.path);
    }
  };

  return (
    <SidebarProvider>
      <Sidebar
        collapsible="none"
        className="sticky top-0 h-screen border-r border-primary bg-subtle"
      >
        <SidebarHeader>
          <SidebarMenu>
            <SidebarMenuItem>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(POST_LOSS_ROUTES.OVERVIEW)}
                className="w-full justify-start gap-2 text-sm font-medium text-secondary hover:bg-background mb-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Exit
              </Button>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild className="hover:bg-background">
                <NavLink to={ADMIN_ROUTES.STYLE_GUIDE} className="no-underline">
                  <span className="font-semibold">Style Guide</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          <SidebarInput
            ref={filterRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setQuery("");
                event.currentTarget.blur();
              }
            }}
            placeholder="Filter components ( / )"
            aria-label="Filter components"
            className="h-9 bg-white"
          />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarGroupContent>
              {visibleSections.length === 0 ? (
                <p className="px-2 py-4 text-sm text-secondary">No components match "{query}"</p>
              ) : (
                <SidebarMenu>
                  {visibleSections.map((section) => (
                    <Collapsible
                      key={section.title}
                      asChild
                      open={isFiltering || openSections.has(section.title)}
                      className="group/collapsible"
                    >
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          onClick={() => handleSectionClick(section)}
                          isActive={isActiveSection(section.path)}
                          className="h-10 !text-sm font-medium hover:!bg-background data-[active=true]:bg-transparent data-[active=true]:font-semibold"
                        >
                          <span>{section.title}</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {section.items.map((item) => (
                              <SidebarMenuSubItem key={item.id}>
                                <SidebarMenuSubButton
                                  asChild
                                  className="h-9 !text-sm hover:bg-background data-[active=true]:bg-background data-[active=true]:font-medium"
                                  isActive={
                                    isActiveSection(section.path) && spiedItemId === item.id
                                  }
                                >
                                  <NavLink
                                    to={`${section.path}#${item.id}`}
                                    className="no-underline"
                                  >
                                    <span>{item.label}</span>
                                  </NavLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  ))}
                </SidebarMenu>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>
      <SidebarInset className="bg-white">
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  );
};

export default StyleGuideLayout;
